import React, { useState, useEffect, useRef } from 'react';
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useNavigate } from 'react-router-dom';
import CountrySelector from '../hooks/CountrySelector';
import BankService from '../../../../services/BankService';

const Step1Form = ({ formData, handleChange, onNext }) => {
    const [errors, setErrors] = useState({});
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [touched, setTouched] = useState({
        issuerBankName: false,
        country: false
    });
    const suggestionRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const allBanks = await BankService.getAllBanks();
                const canadianBanks = allBanks.filter(bank => bank.country === 'Canada');
                setBanks(canadianBanks);
            } catch (error) {
                console.error('Erreur lors de la récupération des banques:', error);
            }
        };
        fetchBanks();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const validate = (fieldName, value) => {
        const newErrors = { ...errors };

        if (fieldName === 'issuerBankName') {
            if (!value || value.trim() === '') {
                newErrors.issuerBankName = 'Veuillez entrer le nom d\'une banque.';
            } else if (value.trim().length < 2) {
                newErrors.issuerBankName = 'Le nom de la banque doit contenir au moins 2 caractères.';
            } else {
                delete newErrors.issuerBankName;
            }
        }

        if (fieldName === 'country') {
            if (!value) {
                newErrors.country = 'Veuillez sélectionner un pays.';
            } else {
                delete newErrors.country;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBankInputChange = (e) => {
        const value = e.target.value;
        handleChange({ target: { name: 'issuerBankName', value } });
        setTouched(prev => ({ ...prev, issuerBankName: true }));

        if (value.trim()) {
            const filtered = banks.filter(bank =>
                bank.bankName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredBanks(filtered);
            setShowSuggestions(true);
        } else {
            setFilteredBanks([]);
            setShowSuggestions(false);
        }

        validate('issuerBankName', value);
    };

    const handleBankSelect = (bankName) => {
        handleChange({ target: { name: 'issuerBankName', value: bankName } });
        setShowSuggestions(false);
        validate('issuerBankName', bankName);
    };

    const handleCountryChange = (value) => {
        handleChange({ target: { name: 'country', value } });
        setTouched(prev => ({ ...prev, country: true }));
        validate('country', value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setTouched({
            issuerBankName: true,
            country: true
        });

        const isIssuerBankValid = validate('issuerBankName', formData.issuerBankName);
        const isCountryValid = validate('country', formData.country);

        if (isIssuerBankValid && isCountryValid) {
            onNext();
        }
    };

    const handleCancel = () => {
        navigate('/transfer');
    };

    return (
        <div className="step-container">
            <h2 className="step-title mb-4">Étape 1: Information de la Banque Émettrice et le pays destinataire</h2>

            <Form noValidate onSubmit={handleSubmit} className="step1-form">
                <div className="form-fields-container">
                    <Form.Group controlId="issuerBankName" className="mb-4">
                        <Form.Label>Nom de la Banque Émettrice</Form.Label>
                        <div className="position-relative" ref={suggestionRef}>
                            <Form.Control
                                type="text"
                                name="issuerBankName"
                                value={formData.issuerBankName}
                                onChange={handleBankInputChange}
                                isInvalid={touched.issuerBankName && !!errors.issuerBankName}
                                isValid={touched.issuerBankName && !errors.issuerBankName && formData.issuerBankName}
                                placeholder="Entrez le nom de la banque ou choisissez dans la liste..."
                                autoComplete="off"
                            />
                            {showSuggestions && filteredBanks.length > 0 && (
                                <div className="position-absolute w-100 mt-1 border rounded shadow-sm"
                                     style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                    {filteredBanks.map((bank) => (
                                        <div
                                            key={bank.id}
                                            className="p-2 cursor-pointer hover:bg-light"
                                            onClick={() => handleBankSelect(bank.bankName)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {bank.bankName}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {touched.issuerBankName && errors.issuerBankName && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {errors.issuerBankName}
                                </Form.Control.Feedback>
                            )}
                            {filteredBanks.length > 0 && showSuggestions && (
                                <small className="text-muted">
                                    Suggestions disponibles. Cliquez pour sélectionner ou continuez votre saisie.
                                </small>
                            )}
                        </div>
                    </Form.Group>

                    <Form.Group controlId="country" className="mb-4">
                        <Form.Label>Sélectionnez le pays du destinataire</Form.Label>
                        <CountrySelector
                            value={formData.country}
                            onChange={handleCountryChange}
                            isInvalid={touched.country && !!errors.country}
                            isValid={touched.country && !errors.country && formData.country}
                        />
                        {touched.country && errors.country && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.country}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </div>

                <div className="form-actions d-flex justify-content-between">
                    <Button variant="secondary" onClick={handleCancel}>
                        Annuler
                    </Button>
                    <Button type="submit" variant="primary" disabled={!!errors.issuerBankName || !!errors.country}>
                        Suivant
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default Step1Form;
